import React, { useEffect, useState } from 'react'
import { push } from '@lagunovsky/redux-react-router'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { Card, CardContent, Stack } from '@mui/material'
import useTitle from 'utils/customHook/useTitle'
import useActions from 'utils/customHook/useActions'
import ProgressCard from 'components/card/ProgressCard'
import ProductionUnitStats from 'productionUnit/components/alerts/ProductionUnitIndicators'
import DashboardStationsPanel from 'station/components/dashboard/component/DashboardStationsPanel'
import DashboardMapPanel from 'station/components/dashboard/component/DashboardMapPanel'
import DashboardAddStationDialog from 'station/components/dashboard/component/DashboardAddStationDialog'
import { MAP, STATION_LIST, STATION_TYPE_NAME } from 'station/constants/StationConstants'
import ProductionUnitAction from 'productionUnit/actions/ProductionUnitAction'
import { H_PRODUCTION_DASHBOARD } from 'account/constants/AccessRulesConstants'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import SimpleTabList from 'components/list/SimpleTabList'
import useProductionUnitsData from 'productionUnit/utils/useProductionUnitsData'
import useProductionUnitsFilter, { PRODUCTION_UNITS_EXPORT_HEADERS, PRODUCTION_UNITS_HEADERS } from 'productionUnit/utils/useProductionUnitsFilter'
import ProductionUnitFilterPanel from './ProductionUnitFilterPanel'
import useSandreList from 'utils/customHook/useSandreList'
import useApplicationSetting, { intParser } from 'utils/customHook/useApplicationSetting'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import { DEFAULT_CONTRIBUTOR_TYPE } from 'administration/components/user/constants/UserConstants'

const stationType = STATION_TYPE_NAME.productionUnit

const ProductionUnitsPanel = () => {
    const {
        productionUnits,
        accountUser,
    } = useSelector(store => ({
        accountUser: store.AccountReducer.accountUser,
        productionUnits: store.ProductionUnitReducer.productionUnits,
    }), shallowEqual)

    const [open, setOpen] = useState(false)

    const dispatch = useDispatch()

    const { data, filterPanelProps, resetFilters, indicators, indicatorsLoaded, indicatorsProgress } = useProductionUnitsFilter()


    const contributorsList = useSandreList(SANDRE.CONTRIBUTOR_TYPES)
    const operatorType = useApplicationSetting('contributorTypeOperator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.OPERATOR
    const referentType = useApplicationSetting('contributorTypeAdministrator', intParser) || DEFAULT_CONTRIBUTOR_TYPE.ADMINISTRATOR

    const productionDynamicHeaders = [
        ...PRODUCTION_UNITS_HEADERS.slice(0, PRODUCTION_UNITS_HEADERS.length / 2),
        contributorsList.find(c => c.code === referentType)?.name,
        contributorsList.find(c => c.code === operatorType)?.name,
        ...PRODUCTION_UNITS_HEADERS.slice(PRODUCTION_UNITS_HEADERS.length / 2),
    ]
    const productionDynamicExportHeaders = [
        ...PRODUCTION_UNITS_EXPORT_HEADERS.slice(0, PRODUCTION_UNITS_EXPORT_HEADERS.length / 2),
        contributorsList.find(c => c.code === referentType)?.name,
        contributorsList.find(c => c.code === operatorType)?.name,
        ...PRODUCTION_UNITS_EXPORT_HEADERS.slice(PRODUCTION_UNITS_EXPORT_HEADERS.length / 2),
    ]

    useActions(() => {
        const defaultActions = {
            export: () => {
                return {
                    data: data.stations,
                    exportType: 'xlsx',
                    titleFile: data.title,
                }
            },
        }
        const newActions = (accountUser.isAdmin === '1' || accountUser.metadata === '1') ? {
            ...defaultActions,
            new: () => setOpen(true),
        } : defaultActions
        return newActions
    }, [data])

    const closeDialog = () => setOpen(false)

    const onValidate = (newElement) => {
        dispatch(ProductionUnitAction.createProductionUnit(newElement, id => {
            closeDialog()
            dispatch(push(`/station/${stationType}/${id}/description`))
        }))
    }

    if (!indicatorsLoaded) {
        return (
            <ProgressCard progress={indicatorsProgress} withMessage />
        )
    }

    return (
        <Stack spacing={1}>
            <ProductionUnitFilterPanel {...filterPanelProps} onReset={resetFilters} />
            <Card>
                <CardContent>
                    <ProductionUnitStats productionUnits={data.stations} indicators={indicators} />
                </CardContent>
            </Card>
            <SimpleTabList
                defaultTab={STATION_LIST}
                tabs={[
                    {
                        constant: STATION_LIST,
                        label: i18n.table,
                        icon: 'list',
                    },
                    {
                        constant: MAP,
                        label: i18n.map,
                        icon: 'map',
                    },
                ]}
                noPadding
            >
                {
                    tab => (
                        <>
                            {tab === STATION_LIST && <DashboardStationsPanel
                                stationType={stationType}
                                data={data}
                                headers={productionDynamicHeaders}
                                exportTableHeaders={productionDynamicExportHeaders}
                            />}
                            {tab === MAP && <DashboardMapPanel
                                stationType={stationType}
                                data={data}
                            />}
                        </>
                    )
                }
            </SimpleTabList>
            <DashboardAddStationDialog
                open={open}
                closeDialog={closeDialog}
                stations={productionUnits}
                title={i18n.newProductionUnit}
                onValidate={onValidate}
            />
        </Stack>
    )
}

const ProductionUnitsDashboard = () => {
    const dispatch = useDispatch()

    useEffect(() => {
        if (!componentHasHabilitations(H_PRODUCTION_DASHBOARD)) {
            dispatch(push('/unauthorized'))
        }
    }, [dispatch])

    const { dataLoaded, progress } = useProductionUnitsData()

    useTitle(() => [{
        title: i18n[stationType],
        href: stationType,
    }, {
        title: i18n.dashboard,
        href: stationType,
    }], [stationType])

    if (!dataLoaded) {
        return (
            <ProgressCard progress={progress} withMessage />
        )
    }

    return <ProductionUnitsPanel />
}

export default ProductionUnitsDashboard
